<template>
    <div class="ext-directory" @click.stop>
        <div class="ext-search">
            <i class="fa fa-magnifying-glass" />
            <input v-model="search" type="text" placeholder="Search extensions" />
        </div>
        <div class="ext-list-wrap">
            <div class="ext-list">
                <div v-for="ext in directoryExtensions" :key="ext.id" class="ext-row-wrap">
                    <button
                        class="ext-row"
                        :class="{ 'is-self': ext.id === selectedFromExtensionId }"
                        :disabled="ext.id === selectedFromExtensionId"
                        v-tooltip="ext.id === selectedFromExtensionId ? 'Your selected extension' : `Call ext. ${ext.extension}`"
                        @click="onClick(ext)"
                    >
                        <span class="ext-dot" :class="dotClass(byExtension.get(ext.id))" />
                        <span class="ext-main">
                            <span class="ext-name">{{ ext.displayName || ext.name }}</span>
                            <span class="ext-sub">{{ ext.extension }} · {{ statusText(byExtension.get(ext.id)) }}</span>
                        </span>
                        <i class="ext-call fa fa-phone" />
                    </button>
                    <!-- Directed pickup: answer the call ringing there from the selected "Call From". Offered on
                         every ringing row — whether this source may actually do so is the target's pickup
                         policy, which only the backend knows; a refusal comes back as a normal call failure. -->
                    <button
                        v-if="canPickUp(ext)"
                        type="button"
                        class="zs-btn zs-btn--ghost zs-btn--icon zs-btn--sm ext-pickup"
                        v-tooltip="`Pick up the call ringing on ext. ${ext.extension}`"
                        @click.stop="emit('pickup', ext, byExtension.get(ext.id)?.callIds[0] ?? null)"
                    >
                        <i class="fa-regular fa-phone-arrow-down-left" />
                    </button>
                </div>
                <div v-if="!directoryExtensions.length" class="ext-empty">
                    {{ search ? 'No matching extensions' : 'No directory extensions' }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

import { formatElapsed } from '@/modules/talk/helpers/duration';
import { presenceMgr, type ExtensionPresence } from '@/modules/talk/shared/presence.manager';
import { type IExtensionResponse } from '@/openapi-clients-generated/talk';
import { DataCache } from '@/shared/data/cache';

const props = defineProps<{
    /** The dialer's "Call From" extension — its own row can't be dialed. */
    selectedFromExtensionId?: string | null;
}>();

const emit = defineEmits<{ dial: [ext: IExtensionResponse]; pickup: [ext: IExtensionResponse, callId: string | null] }>();

// Hold the presence feed (snapshot + delta subscription) open while this pane is mounted.
presenceMgr.use();
const byExtension = presenceMgr.byExtension;

const extensions = DataCache.getRef('talkExtensions');
const search = ref('');

// 1s tick so "On call · m:ss" timers advance live.
const now = ref(Date.now());
let timer: ReturnType<typeof setInterval> | undefined;
onMounted(() => {
    timer = setInterval(() => (now.value = Date.now()), 1000);
});
onUnmounted(() => {
    if (timer) clearInterval(timer);
});

const directoryExtensions = computed(() => {
    // Voicemail-only extensions (no SIP endpoint) have no live presence and can't
    // ring a device, so they don't belong in the presence directory.
    const all = (extensions.value ?? []).filter(e => !e.deletedAt && e.includeInDirectory && e.enableSip);
    const q = search.value.trim().toLowerCase();
    const filtered = q
        ? all.filter(
              e => e.extension.toLowerCase().includes(q) || e.name.toLowerCase().includes(q) || (e.displayName?.toLowerCase().includes(q) ?? false)
          )
        : all;
    return [...filtered].sort((a, b) => a.extension.localeCompare(b.extension, undefined, { numeric: true }));
});

function dotClass(p?: ExtensionPresence): string {
    if (!p) return 'is-idle';
    if (p.state === 'on-call') return 'is-busy';
    if (p.state === 'held') return 'is-held';
    return 'is-ringing';
}

function statusText(p?: ExtensionPresence): string {
    if (!p) return 'Available';
    if (p.state === 'ringing') return 'Ringing…';
    if (p.state === 'held') return p.callCount > 1 ? 'On hold +1' : 'On hold';
    return `On call · ${elapsed(p.since)}`;
}

function elapsed(since: number): string {
    // Tolerate either ms- or seconds-based source timestamps.
    const sinceMs = since > 1e12 ? since : since * 1000;
    return formatElapsed(sinceMs, now.value);
}

function onClick(ext: IExtensionResponse) {
    emit('dial', ext);
}

/** A ringing extension other than the caller's own can be picked up by dialing `**<ext>`. */
function canPickUp(ext: IExtensionResponse): boolean {
    return ext.id !== props.selectedFromExtensionId && byExtension.value.get(ext.id)?.state === 'ringing';
}
</script>

<style scoped>
@reference '#tw.css';

.ext-directory {
    @apply flex flex-col min-h-0 min-w-0;
}

.ext-search {
    @apply flex items-center gap-2 px-2 mb-2 bg-chrome-panel-raised rounded text-ink-on-chrome-2;

    input {
        @apply flex-1 min-w-0 bg-transparent border-none text-sm text-white py-2 focus:outline-none;

        &::placeholder {
            @apply text-ink-on-chrome-2;
        }
    }

    i {
        @apply text-xs text-ink-on-chrome-2;
    }
}

/* Take the scroll area out of flow so the directory never grows the popover past
   the dialer's height; the dialer pane defines the height and the list scrolls within it. */
.ext-list-wrap {
    @apply relative flex-1 min-h-0;
}

.ext-list {
    @apply absolute inset-0 flex flex-col gap-0 overflow-y-auto;
}

.ext-row-wrap {
    @apply flex items-center gap-1;
}

/* Dark surface: ghost is visible here, and the hover wash matches the row's own. */
.ext-pickup {
    @apply flex-shrink-0 text-warn-fill;

    &:hover {
        @apply bg-chrome-panel-raised;
    }
}

.ext-row {
    @apply flex items-center gap-2 flex-1 min-w-0 h-auto px-2 py-1 bg-transparent rounded border-none cursor-pointer text-left;

    &:hover:not(.is-self) {
        @apply bg-chrome-panel-raised;
    }

    &.is-self {
        @apply opacity-60 cursor-default;
    }
}

.ext-dot {
    @apply w-2.5 h-2.5 rounded-full flex-shrink-0;

    &.is-idle {
        @apply bg-ok-fill;
    }

    &.is-busy {
        @apply bg-err-fill;
    }

    &.is-held {
        @apply bg-warn-fill;
    }

    &.is-ringing {
        @apply bg-warn-fill;
        animation: ext-pulse 1s ease-in-out infinite;
    }
}

.ext-main {
    @apply flex flex-col min-w-0 flex-1;
}

.ext-name {
    @apply text-sm text-white truncate;
}

.ext-sub {
    @apply text-xs text-ink-on-chrome-2 truncate;
}

.ext-call {
    @apply text-xs text-ink-on-chrome-2 opacity-0 flex-shrink-0;
}

.ext-row:hover:not(.is-self) .ext-call {
    @apply opacity-100 text-ok-fill;
}

.ext-empty {
    @apply text-xs text-ink-on-chrome-2 text-center py-6;
}

@keyframes ext-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.3;
    }
}
</style>
